// One requisition: header, the action this person can take right now, the
// lines, and the timeline of who did what (kitchen flow, lane C). Kitchen
// actions (send, cancel, receive) go through the offline queue; store and
// admin actions are sent directly. Every action carries one Idempotency-Key
// per intent (requisition + action + version), reused when it is retried.
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'
import apiClient from '@/api/client'
import { friendlyError } from '@/lib/inventoryFlow/friendlyError'
import { formatMoney } from '@/lib/currency'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn, formatDateHuman } from '@/lib/utils'
import { fmtQty } from '@/lib/quantity'
import { T, eventLabel, kindLabel } from '@/lib/inventoryFlow/strings'
import { DocumentTimeline } from './DocumentTimeline'
import {
  receiveBody,
  receiveError,
  receiveLinesFor,
  receiveMode,
  receiveNeedsReason,
  receiveReady,
  receiveStart,
  type ShortChoice,
} from '@/lib/inventoryFlow/drafts'
import { queuedFor, type QueuedAction, type QueuedMethod, type RunOptions } from '@/lib/inventoryFlow/offlineQueue'
import { intentScope, useIntentKeys, type IntentKeys } from '@/lib/inventoryFlow/intentKey'
import type { KitchenActionResult } from '@/lib/inventoryFlow/useKitchenActions'
import type { Requisition, RequisitionLine } from '@/lib/inventoryFlow/types'
import { ItemAvatar, StatusChip } from './StatusChip'
import { QtyStepper } from './QtyStepper'
import { ApproveSendBlock, SendRestBlock } from './StoreSendBlocks'
import { LoadError } from './LoadError'
import { neededHeader } from '@/lib/inventoryFlow/storeScreenLogic'
import { BuyShortfallButton } from '@/components/store/purchasing/BuyShortfallButton'

export type KitchenRun = <R>(label: string, method: QueuedMethod, path: string, body: unknown, opts?: RunOptions) => Promise<KitchenActionResult<R>>
type Notify = (kind: 'success' | 'error', message: string) => void

function errMessage(e: unknown): string {
  return friendlyError(e)
}

export function RequisitionPanel({
  id,
  role,
  kitchenRun,
  kitchenQueue,
  notify,
  storeView = false,
  onThrowAway,
}: {
  id: string
  role: string
  kitchenRun?: KitchenRun
  /** What is waiting on this tablet (useKitchenActions().pending). */
  kitchenQueue?: QueuedAction[]
  notify: Notify
  /** The store's To do (batch 4): event title and plain-words needed-by in the header. */
  storeView?: boolean
  /** Opens where expired stock is written off (Alerts). */
  onThrowAway?: () => void
}) {
  const qc = useQueryClient()
  const keys = useIntentKeys()
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['inventory-flow', 'requisition', id],
    queryFn: () => apiClient.getRequisition(id),
  })
  const r = data?.data
  if (isLoading) return <p className="p-4 text-muted-foreground">{T.loading}</p>
  if (isError || !r) return <LoadError busy={isFetching} onRetry={() => void refetch()} />

  const isAdmin = role === 'admin'
  const store = isAdmin || role === 'inventory_manager'
  const kitchen = isAdmin || role === 'kitchen_manager'
  const lines = r.lines ?? []
  const issuedSum = lines.reduce((s, l) => s + l.issued_qty, 0)
  const canApprove = store && r.kind === 'issue' && r.status === 'submitted'
  const canIssue = store && r.kind === 'issue' && (r.status === 'approved' || r.status === 'partially_issued')
  const canReceive =
    (r.kind === 'issue' ? kitchen : store) &&
    ['partially_issued', 'issued', 'received'].includes(r.status) &&
    lines.some((l) => l.in_transit_qty > 1e-6)
  const canCancel = kitchen && ['draft', 'submitted', 'approved'].includes(r.status) && issuedSum <= 1e-6
  const canSubmit = kitchen && r.status === 'draft'
  const refresh = () => void qc.invalidateQueries({ queryKey: ['inventory-flow'] })
  const actionPath = (action: string) => `/inventory-flow/requisitions/${r.id}/${action}`
  // A kitchen action already waiting on this tablet for this request: the
  // button waits too, so a re-tap cannot queue a second copy.
  const waiting = (action: string) => !!kitchenQueue && !!queuedFor(kitchenQueue, 'POST', actionPath(action))

  const kitchenAction = async (label: string, action: string, body: unknown, okMsg: string) => {
    const path = actionPath(action)
    const scope = intentScope(action, r.id, r.updated_at)
    const key = keys.keyFor(scope)
    try {
      if (kitchenRun) {
        const res = await kitchenRun(label, 'POST', path, body, { key, exclusive: true })
        notify('success', res.status === 'queued' ? T.queued : okMsg)
        if (res.status === 'sent') keys.renew(scope)
      } else {
        await apiClient.inventoryFlowMutate('POST', path, body, key)
        keys.renew(scope)
        notify('success', okMsg)
      }
      refresh()
      return true
    } catch (e) {
      notify('error', errMessage(e))
      refresh()
      return false
    }
  }

  return (
    <div className="space-y-5">
      {storeView ? (
        <header className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold">{r.production_plan_title || r.req_no}</h2>
            <StatusChip status={r.status} kind={r.kind} />
          </div>
          <p className="text-sm text-muted-foreground">
            {[
              r.kind === 'issue' && r.needed_by ? neededHeader(r.needed_by) : kindLabel(r.kind),
              `${r.from_location.name} → ${r.to_location.name}`,
              r.requested_by_name ? `${T.askedBy} ${r.requested_by_name}` : null,
              r.production_plan_title ? r.req_no : null,
            ].filter(Boolean).join(' · ')}
          </p>
          {r.status !== 'submitted' && <FillBar fill={r.fill} />}
          {r.notes && <p className="rounded-md bg-muted/50 p-2 text-sm">{r.notes}</p>}
        </header>
      ) : (
      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-bold">{r.req_no}</h2>
          <StatusChip status={r.status} kind={r.kind} />
          <span className="text-sm text-muted-foreground">{kindLabel(r.kind)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">{r.from_location.name}</span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" aria-label={T.to} />
          <span className="font-medium">{r.to_location.name}</span>
          {r.needed_by && <span className="text-sm text-muted-foreground">· {T.neededBy.replace(' (optional)', '')}: {formatDateHuman(r.needed_by)}</span>}
        </div>
        <FillBar fill={r.fill} />
        {r.notes && <p className="rounded-md bg-muted/50 p-2 text-sm">{r.notes}</p>}
      </header>
      )}

      {canApprove && <ApproveSendBlock r={r} keys={keys} notify={notify} onDone={refresh} onThrowAway={onThrowAway} />}
      {canIssue && <SendRestBlock r={r} keys={keys} notify={notify} onDone={refresh} />}
      {canReceive && (
        <ReceiveBlock r={r} waiting={waiting('receive')} onSave={(body) => kitchenAction(`${T.receive} ${r.req_no}`, 'receive', body, T.receiveOk)} />
      )}
      {(canSubmit || canCancel) && (
        <div className="flex flex-wrap items-center gap-2">
          {canSubmit && (
            <Button disabled={waiting('submit')} onClick={() => void kitchenAction(`${T.submitDraft} ${r.req_no}`, 'submit', {}, T.sentOk)}>
              {T.submitDraft}
            </Button>
          )}
          {canCancel && (
            <Button variant="outline" disabled={waiting('cancel')} onClick={() => void kitchenAction(`${T.cancel} ${r.req_no}`, 'cancel', {}, T.cancelOk)}>
              {T.cancel}
            </Button>
          )}
          {(waiting('submit') || waiting('cancel')) && <p className="text-sm text-amber-700 dark:text-amber-300">{T.waitingToSend}</p>}
        </div>
      )}

      <LinesTable r={r} keys={keys} canWriteOff={isAdmin} notify={notify} onDone={refresh} />
      {store && r.kind === 'issue' && ['approved', 'partially_issued'].includes(r.status) && <ShortfallCard id={r.id} notify={notify} />}
      <Timeline r={r} />
    </div>
  )
}

function FillBar({ fill }: { fill: Requisition['fill'] }) {
  return (
    <div className="flex flex-wrap gap-4 text-sm">
      <span>
        {T.sent}: <strong>{fill.issued_pct}%</strong>
      </span>
      <span>
        {T.got}: <strong>{fill.received_pct}%</strong>
      </span>
    </div>
  )
}

function ReceiveBlock({ r, waiting, onSave }: { r: Requisition; waiting: boolean; onSave: (body: unknown) => Promise<boolean> }) {
  const mode = receiveMode(r.lines ?? [])
  const lines = useMemo(() => receiveLinesFor(mode, r.lines ?? []), [mode, r.lines])
  const [got, setGot] = useState<Record<string, number>>({})
  const [why, setWhy] = useState<Record<string, string>>({})
  const [choice, setChoice] = useState<Record<string, ShortChoice | undefined>>({})
  const [busy, setBusy] = useState(false)
  // Found goods are the exception: the form stays folded away behind one
  // small button so a finished delivery does not look like unfinished work.
  const [foundOpen, setFoundOpen] = useState(false)
  useEffect(() => {
    setGot(Object.fromEntries(lines.map((l) => [l.id, receiveStart(mode, l)])))
    setWhy({})
    setChoice({})
    setFoundOpen(false)
  }, [r.id, r.updated_at, mode]) // eslint-disable-line react-hooks/exhaustive-deps

  if (mode === 'none') return null
  if (mode === 'found' && !foundOpen && !waiting) {
    return (
      <Button variant="outline" onClick={() => setFoundOpen(true)}>
        {T.foundOpen}
      </Button>
    )
  }

  const ready = receiveReady(mode, lines, got, why, choice)
  const save = async () => {
    setBusy(true)
    await onSave({ lines: receiveBody(mode, lines, got, why, choice) })
    setBusy(false)
  }

  return (
    <section className="space-y-3 rounded-lg border-2 border-emerald-300 p-4 dark:border-emerald-800">
      <h3 className="text-base font-semibold">{mode === 'found' ? T.foundTitle : T.receive}</h3>
      {mode === 'found' && <p className="text-sm text-muted-foreground">{T.foundHelp}</p>}
      {lines.map((l) => {
        const v = got[l.id] ?? 0
        const err = receiveError(l, v)
        return (
          <div key={l.id} className="space-y-2 border-b pb-3 last:border-0">
            <div className="flex flex-wrap items-center gap-3">
              <ItemAvatar name={l.item_name} />
              <div className="min-w-[10rem] flex-1">
                <div className="font-medium">{l.item_name}</div>
                <div className="text-sm text-muted-foreground">
                  {mode === 'found'
                    ? `${T.missing} ${fmtQty(l.short_received_qty)} ${l.unit}`
                    : `${T.sent} ${fmtQty(l.awaiting_receipt_qty)} ${l.unit}`}
                </div>
              </div>
              <QtyStepper value={v} unit={l.unit} label={l.item_name}
                max={mode === 'found' ? l.short_received_qty : l.in_transit_qty}
                onChange={(n) => setGot((s) => ({ ...s, [l.id]: n }))} />
            </div>
            {err && <p className="text-sm text-destructive">{err}</p>}
            {receiveNeedsReason(l, v) && mode === 'arrivals' && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">{T.shortBy(fmtQty(l.awaiting_receipt_qty - v), l.unit)}</p>
                <div className="flex w-fit flex-wrap gap-1 rounded-lg bg-muted/60 p-1" role="radiogroup" aria-label={T.shortBy(fmtQty(l.awaiting_receipt_qty - v), l.unit)}>
                  {(['coming', 'missing'] as const).map((c) => (
                    <button key={c} type="button" role="radio" aria-checked={choice[l.id] === c}
                      onClick={() => setChoice((s) => ({ ...s, [l.id]: c }))}
                      className={cn('rounded-md px-4 py-2 text-sm font-medium transition-colors',
                        choice[l.id] === c ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                      {c === 'coming' ? T.restComing : T.restMissing}
                    </button>
                  ))}
                </div>
                {choice[l.id] === 'coming' && <p className="text-xs text-muted-foreground">{T.restComingHelp}</p>}
                {choice[l.id] === 'missing' && (
                  <Input placeholder={T.whyLess} value={why[l.id] ?? ''} autoFocus onChange={(e) => setWhy((s) => ({ ...s, [l.id]: e.target.value }))} />
                )}
              </div>
            )}
            {receiveNeedsReason(l, v) && mode !== 'arrivals' && (
              <Input placeholder={T.whyLess} value={why[l.id] ?? ''} onChange={(e) => setWhy((s) => ({ ...s, [l.id]: e.target.value }))} />
            )}
          </div>
        )
      })}
      <div className="flex gap-2">
        <Button className="flex-1" disabled={!ready || busy || waiting} onClick={() => void save()}>{busy ? T.sending : T.receiveSave}</Button>
        {mode === 'found' && (
          <Button variant="ghost" onClick={() => setFoundOpen(false)}>{T.close}</Button>
        )}
      </div>
      {waiting && <p className="text-sm text-amber-700 dark:text-amber-300">{T.waitingToSend}</p>}
    </section>
  )
}

function LinesTable({ r, keys, canWriteOff, notify, onDone }: { r: Requisition; keys: IntentKeys; canWriteOff: boolean; notify: Notify; onDone: () => void }) {
  const [woLine, setWoLine] = useState<RequisitionLine | null>(null)
  const [pin, setPin] = useState('')
  const woScope = (l: RequisitionLine) => intentScope('write-off', r.id, l.id, r.updated_at)
  const writeOff = useMutation({
    mutationFn: (l: RequisitionLine) => apiClient.requisitionLineAction(r.id, l.id, 'write-off', { pin }, keys.keyFor(woScope(l))),
    onSuccess: (_res, l) => { keys.renew(woScope(l)); setWoLine(null); notify('success', T.writeOffOk); onDone() },
    onError: (e) => { notify('error', errMessage(e)); onDone() },
    // A PIN is never left sitting in the box, right or wrong.
    onSettled: () => setPin(''),
  })
  const cols = [T.asked, T.approved, T.sent, T.got, T.missing]
  return (
    <section className="overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[34rem] text-sm">
        <thead className="bg-muted/50 text-left">
          <tr>
            <th className="p-2.5">Item</th>
            {cols.map((c) => <th key={c} className="p-2.5 text-right">{c}</th>)}
            {canWriteOff && <th className="p-2.5" />}
          </tr>
        </thead>
        <tbody>
          {(r.lines ?? []).map((l) => (
            <tr key={l.id} className="border-t align-top">
              <td className="p-2.5">
                <div className="font-medium">{l.item_name}</div>
                {!!l.requested_value && <div className="text-xs text-muted-foreground tabular-nums">≈ {formatMoney(l.requested_value)}</div>}
                {l.short_reason && <div className="text-xs text-muted-foreground">{l.closed_short ? `${T.closeShort}: ` : ''}{l.short_reason}</div>}
                {l.receive_variance_reason && <div className="text-xs text-red-700 dark:text-red-300">{l.receive_variance_reason}</div>}
              </td>
              <td className="whitespace-nowrap p-2.5 text-right">{fmtQty(l.requested_qty)} {l.unit}</td>
              <td className="whitespace-nowrap p-2.5 text-right">{l.approved_qty == null ? '—' : `${fmtQty(l.approved_qty)} ${l.unit}`}</td>
              <td className="whitespace-nowrap p-2.5 text-right">{fmtQty(l.issued_qty)} {l.unit}</td>
              <td className="whitespace-nowrap p-2.5 text-right">{fmtQty(l.received_qty)} {l.unit}</td>
              <td className={cn('whitespace-nowrap p-2.5 text-right', l.short_received_qty > 0 && 'font-semibold text-red-700 dark:text-red-300')}>
                {l.short_received_qty > 0 ? `${fmtQty(l.short_received_qty)} ${l.unit}` : l.written_off_qty > 0 ? `${fmtQty(l.written_off_qty)} ${l.unit} ✕` : '—'}
              </td>
              {canWriteOff && (
                <td className="p-2.5 text-right">
                  {l.short_received_qty > 0 && (
                    <Button size="sm" variant="outline" onClick={() => { setWoLine(l); setPin('') }}>{T.writeOff}</Button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {woLine && (
        <div className="flex flex-wrap items-end gap-2 border-t bg-muted/30 p-3">
          <div className="space-y-1">
            <Label htmlFor="wo-pin">{T.writeOffPin} — {woLine.item_name} ({fmtQty(woLine.short_received_qty)} {woLine.unit})</Label>
            <Input id="wo-pin" type="password" inputMode="numeric" autoComplete="off" className="w-40" value={pin} onChange={(e) => setPin(e.target.value)} />
          </div>
          <Button variant="destructive" disabled={!pin || writeOff.isPending} onClick={() => writeOff.mutate(woLine)}>{T.writeOff}</Button>
          <Button variant="ghost" onClick={() => setWoLine(null)}>{T.close}</Button>
        </div>
      )}
    </section>
  )
}

// Only shown to store roles (admin / inventory manager) — the same roles the
// from-suggestions endpoint admits.
function ShortfallCard({ id, notify }: { id: string; notify: Notify }) {
  const { data } = useQuery({
    queryKey: ['inventory-flow', 'shortfall', id],
    queryFn: () => apiClient.getRequisitionShortfall(id),
    // Fresh each time: already-ordered stock is netted out server-side, and a
    // stale list would offer to buy it again.
    staleTime: 0,
  })
  const groups = data?.data?.suppliers ?? []
  return (
    <section className="space-y-2 rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold">{T.buyList}</h3>
        {/* Only items with a supplier can become orders; without one the button would make nothing. */}
        {groups.some((g) => g.supplier_id) && <BuyShortfallButton requisitionId={id} showToast={notify} />}
      </div>
      {groups.length === 0 ? (
        <p className="text-sm text-muted-foreground">{T.nothingToBuy}</p>
      ) : (
        groups.map((g) => (
          <div key={g.supplier_id ?? 'none'}>
            <div className="text-sm font-medium">{g.supplier_name ?? T.noSupplier}</div>
            {!g.supplier_id && <p className="text-sm text-muted-foreground">{T.noSupplierHint}</p>}
            <ul className="ml-4 list-disc text-sm">
              {g.lines.map((l) => <li key={l.line_id}>{l.item_name}: {fmtQty(l.shortfall_qty)} {l.unit}</li>)}
            </ul>
          </div>
        ))
      )}
    </section>
  )
}

function Timeline({ r }: { r: Requisition }) {
  const items = (r.events ?? []).map((e) => ({
    key: e.id, at: e.created_at, label: eventLabel(e.event_type), what: e.item_name ?? undefined,
    actorName: e.actor_name, authorizedByName: e.authorized_by_name, note: e.note,
  }))
  return <DocumentTimeline title={T.timeline} items={items} by={T.by} pinBy={T.pinBy} />
}
